import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  CampusIdFailed,
  CampusIdInit,
  CampusIdInvalidate,
  CampusIdStore,
  CampusIdValid,
  CampusIdValidate,
  CampusIdValidateFailed,
} from './campus-id.actions';
import { CampusIdService, TOTPValidation } from './campus-id.service';
import { AlertController, LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CampusIdEffects {
  private actions$ = inject(Actions);
  private campusIdService = inject(CampusIdService);
  private alertController = inject(AlertController);
  private translate = inject(TranslateService);
  private loadingController = inject(LoadingController);

  private loadingSpinner = null;

  campusIdInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampusIdInit),
      switchMap(() =>
        from(
          this.loadingController.create({
            message: this.translate.instant('campus-id.initializing'),
            duration: 10000,
          })
        ).pipe(
          switchMap((spinner) => {
            this.loadingSpinner = spinner;
            return from(this.loadingSpinner.present()).pipe(
              switchMap(() =>
                this.campusIdService.requestTOTP().pipe(
                  map((totpSecret: { uuid: string; secret: string }) => {
                    if (this.loadingSpinner) {
                      this.loadingSpinner.dismiss();
                      this.loadingSpinner = null;
                    }
                    return CampusIdStore({ ...totpSecret });
                  }),
                  catchError((error: HttpErrorResponse) => {
                    if (this.loadingSpinner) {
                      this.loadingSpinner.dismiss();
                      this.loadingSpinner = null;
                    }
                    return of(CampusIdFailed({ error }));
                  })
                )
              )
            );
          })
        )
      )
    )
  );

  campusIdValidate = createEffect(() =>
    this.actions$.pipe(
      ofType(CampusIdValidate),
      switchMap((props: { uuid: string; totp: string }) =>
        from(
          this.loadingController.create({
            message: this.translate.instant('campus-id.validating'),
            duration: 10000,
          })
        ).pipe(
          switchMap((spinner) => {
            this.loadingSpinner = spinner;
            return from(this.loadingSpinner.present()).pipe(
              switchMap(() =>
                this.campusIdService.validate(props.uuid, props.totp).pipe(
                  map((totpValidation: TOTPValidation) => {
                    if (this.loadingSpinner) {
                      this.loadingSpinner.dismiss();
                      this.loadingSpinner = null;
                    }

                    if (!totpValidation.valid) {
                      return CampusIdInvalidate();
                    }
                    return CampusIdValid();
                  }),
                  catchError((error: HttpErrorResponse) => {
                    if (this.loadingSpinner) {
                      this.loadingSpinner.dismiss();
                      this.loadingSpinner = null;
                    }
                    return of(CampusIdValidateFailed({ error }));
                  })
                )
              )
            );
          })
        )
      )
    )
  );

  campusIdInvalidate = createEffect(() =>
    this.actions$.pipe(
      ofType(CampusIdInvalidate),
      map(() => CampusIdInit())
    )
  );

  campusIdFailAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CampusIdFailed),
        tap(async (action) => {
          const alert = await this.alertController.create({
            header: this.translate.instant('core.errors.errorLabel'),
            subHeader: this.translate.instant('campus-id.setupFailed.message'),
            message: `${action.error?.message}` || this.translate.instant('core.errors.unknownError'),
            buttons: [this.translate.instant('core.ok')],
          });
          await alert.present();
        })
      ),
    { dispatch: false }
  );

  campusIdValidateFailedAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CampusIdValidateFailed),
        tap(async (action) => {
          const alert = await this.alertController.create({
            header: this.translate.instant('core.errors.errorLabel'),
            subHeader: this.translate.instant('campus-id.validateFailed.message'),
            message: `${action.error?.message}` || this.translate.instant('core.errors.unknownError'),
            buttons: [this.translate.instant('core.ok')],
          });
          await alert.present();
        })
      ),
    { dispatch: false }
  );
}
